import { Badge, Button, cn } from '@parcel/ui';
import * as React from 'react';

export interface ChipItem {
  key: string;
  label: string;
  title?: string;
}

interface ChipListProps {
  items: readonly ChipItem[];
  /** Noun for the summary line, singular and plural. */
  noun: [string, string];
  /** Shown in place of the summary when there are no items. */
  emptyText: string;
  mono?: boolean;
  testId?: string;
}

/** A one-line count with a Show link; the chips only render once asked for. */
export function ChipList({ items, noun, emptyText, mono = false, testId }: ChipListProps) {
  const [open, setOpen] = React.useState(false);
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={testId}>
        {emptyText}
      </p>
    );
  }
  const count = `${items.length} ${items.length === 1 ? noun[0] : noun[1]}`;
  return (
    <div className="space-y-2" data-testid={testId}>
      <p className="text-sm text-muted-foreground">
        {count}
        <Button
          type="button"
          variant="link"
          size="sm"
          className="ml-2 h-auto p-0 text-xs"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </p>
      {open ? (
        <ul className="flex flex-wrap gap-1.5">
          {items.map((item) => (
            <li key={item.key}>
              <Badge
                variant="secondary"
                title={item.title}
                className={cn('font-normal', mono && 'font-mono text-[12px]')}
              >
                {item.label}
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** HUBSPOT_SEARCH_CONTACTS_BY_CRITERIA becomes "Search contacts by criteria". */
export function humanizeToolSlug(slug: string, providerKey?: string): string {
  const split = (value: string) =>
    value
      .toLowerCase()
      .split(/[_\s]+/)
      .filter(Boolean);
  let words = split(slug);
  const prefix = providerKey ? split(providerKey) : [];
  const startsWithPrefix = () =>
    prefix.length > 0 && prefix.every((word, index) => words[index] === word);
  while (startsWithPrefix()) words = words.slice(prefix.length);
  const [first, ...rest] = words;
  if (!first) return slug;
  return first.charAt(0).toUpperCase() + first.slice(1) + (rest.length ? ` ${rest.join(' ')}` : '');
}
